import { buildProduct } from '@hive/platform-url-schema'
import React, { SyntheticEvent, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'

import { isAgeRestrictedProduct } from '@entities/Product/helpers'

import { CartProduct } from '@features/cart/model/useCartStore'

import { PRODUCT_NO_PHOTO_PATH } from '@shared/contants/product'
import { Product } from '@shared/generated'
import { useAgeVerification } from '@shared/hooks'
import { cn } from '@shared/lib/cn'
import ButtonDelay from '@shared/ui/button-delay'

import ActionButtons from './action-buttons'
import PriceBlock from './price-block'
import ControlCart from '../control'
import DeleteProduct from '../delete-product'

import styles from './styles.module.scss'

import type { MouseEventHandler } from 'react'

export interface CartPositionProps {
  key?: string
  item?: CartProduct
  product: Product
  onOutOfStockPositionClick: (e: SyntheticEvent, item: CartProduct | undefined, product: Product) => void
  onLinkClick?: (e: SyntheticEvent, item: CartProduct | undefined, product: Product) => void
  onRemoveRequested: (e: SyntheticEvent | null, id: string) => void
}

const CartPosition: React.FC<CartPositionProps> = ({
  key,
  item,
  product,
  onOutOfStockPositionClick,
  onLinkClick,
  onRemoveRequested,
}) => {
  const OUT_STOCK = !item?.amount
  const navigate = useNavigate()

  const { t: tca } = useTranslation('cart')
  const { isAdult, guardAgeRestrictedAction } = useAgeVerification()

  const [ deleting, setDeleting ] = useState(false)
  const productUrl = buildProduct(product.slug as string)
  const isAgeControlRequired = !isAdult && isAgeRestrictedProduct(product)

  const handleLinkClick: MouseEventHandler<HTMLAnchorElement> = useCallback((e) => {
    if (OUT_STOCK) {
      return onOutOfStockPositionClick(e, item, product)
    }

    onLinkClick?.(e, item, product)

    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return
    }

    e.preventDefault()

    guardAgeRestrictedAction(isAgeControlRequired, () => {
      navigate(productUrl)
    })
  }, [
    OUT_STOCK,
    onOutOfStockPositionClick,
    onLinkClick,
    item,
    product,
    guardAgeRestrictedAction,
    isAgeControlRequired,
    navigate,
    productUrl,
  ])

  const handleDeleteRequested = useCallback((e: SyntheticEvent) => {
    e.preventDefault()
    setDeleting(true)
  }, [ setDeleting ])

  return (
    <div
      className={cn(styles['preview-cart-item'], {
        [styles.out]: OUT_STOCK,
      })}
      key={key}
    >
      {/* Product Image */}
      <Link
        className={styles['preview-cart-item__image']}
        onClick={handleLinkClick}
        to={productUrl}
      >
        <img
          src={product.preview?.url || PRODUCT_NO_PHOTO_PATH}
          onError={(e) => {
            const target = e.currentTarget
            if (
              target.src !==
              window.location.origin + PRODUCT_NO_PHOTO_PATH
            ) {
              target.src = PRODUCT_NO_PHOTO_PATH
            }
          }}
          alt=""
        />
      </Link>

      {/* Product Content */}
      <div className={styles['preview-cart-item__content']}>
        {/* Product Name */}
        <Link
          className={styles['preview-cart-item__title']}
          to={productUrl}
          onClick={handleLinkClick}
        >
          {product.name}
        </Link>

        {/* Quantity Controls */}
        {!OUT_STOCK && (
          <div className={styles['preview-cart-item__controls']}>
            <ControlCart isTablet={true} product={product} noTotals />
          </div>
        )}
      </div>

      {/* Price and Action Buttons */}
      <div className={styles['preview-cart-item__right']}>
        {/* Price Block */}
        {!OUT_STOCK && (
          <div className={styles['preview-cart-item__price-section']}>
            <PriceBlock product={product} hideOldPrice showTotalPriceDiscount />
          </div>
        )}

        {/* Action Buttons */}
        <div className={styles['preview-cart-item__actions']}>
          {deleting
            ? (
              <ButtonDelay
                duration={3000}
                onFinish={() => onRemoveRequested(null, product.id)}
                onClick={() => {
                  setDeleting(false)
                }}
              >
                {tca('preview.cart_return')}
              </ButtonDelay>
            )
            : (
            !OUT_STOCK ? (
                <ActionButtons product={product} onRemove={handleDeleteRequested} size='medium' buttonsTemplate={[ 'comment', 'favorite', 'delete' ]} />
              ) : (
                <DeleteProduct
                  className={styles['preview-cart-item__delete']}
                  onClick={handleDeleteRequested}
                />
              )
            )
          }
        </div>
      </div>
    </div>
  )
}

export default CartPosition
